import JSONSchemaForm, { type FormProps, type IChangeEvent } from "@rjsf/core";
import type {
	ErrorSchema,
	FieldTemplateProps,
	RJSFSchema,
	RJSFValidationError,
	UiSchema,
	ValidatorType,
} from "@rjsf/utils";
import validator from "@rjsf/validator-ajv8";
import cx from "classnames";
import React from "react";
import * as styles from "./FormBuilder.module.scss";

import { Button } from "../../../components/common";
import { useClientSide } from "../../../hooks/useClientSide";
import AddressManagerProvider from "../AddressManager/AddressManager";
import Label from "../Label/Label";
import defaultWidgets from "../widgets";
import {
	FormBuilderContextProvider,
	useFormBuilderContext,
} from "./FormBuilder.context";

interface Props extends FormProps {
	schema: RJSFSchema;
	uiSchema: UiSchema;
	errorSchema: ErrorSchema;
	formData?: unknown;
	// biome-ignore lint/suspicious/noExplicitAny: Legit any
	aerValidator?: ValidatorType<unknown, RJSFSchema, any>;
	isLastStep?: boolean;
	previousStepUrl?: string;
	// biome-ignore lint/suspicious/noExplicitAny: Legit any
	onChange: (e: IChangeEvent<unknown, RJSFSchema, any>) => void;
	hasSubmitted?: boolean;
}

// Template Changes

const ErrorListTemplate: React.FC = () => {
	// Insert the error message above the button with id "submit-button"
	const errorMessage = document.createElement("div");
	errorMessage.id = "error-summary";
	errorMessage.className = styles.errorMessage;
	errorMessage.innerHTML = "Please fill in required information.";
	document
		.getElementById("submit-button")
		?.insertAdjacentElement("beforebegin", errorMessage);
	return null;
};

const CustomFieldTemplate: React.FC<FieldTemplateProps> = ({
	id,
	classNames,
	style,
	label,
	help,
	required,
	description,
	errors,
	children,
	uiSchema,
}) => {
	// This is the title - for some reason it uses the field template - we don't want it to so we hide it.
	let showLabel = true;
	if (uiSchema["ui:options"]?.label === false) showLabel = false;
	if (id === "root" || id === "root__title") showLabel = false;

	const tooltip = uiSchema["ui:customHelp"];
	return (
		<div
			className={cx(
				classNames,
				styles.fieldSpacing,
				uiSchema["ui:options"]?.isConditional && styles.conditionalbackground,
				uiSchema["ui:options"]?.backgroundTheme === "white"
					? styles.whitebg
					: styles.beigebg,
			)}
			style={{
				...style,
				// biome-ignore lint/suspicious/noExplicitAny: Cannot get types from rjsf
				display: (["UserId", "DogPicker"] as any[]).includes(
					uiSchema["ui:widget"],
				)
					? "none"
					: undefined,
			}}
		>
			{showLabel && (
				<Label id={id} label={label} required={required} tooltip={tooltip} />
			)}
			{description}
			{children}
			{errors}
			{help}
		</div>
	);
};

interface PropertyMessages {
	[key: string]: string;
}

function transformErrors(errors: RJSFValidationError[]) {
	const getMessageForProperty = (propertyName: string) => {
		const messages: PropertyMessages = {
			CONTACT_FIRSTNAME: "Please tell us your first name",
			CONTACT_SURNAME: "Please tell us your surname",
			ADOPTION_SEC_CONTACT_FIRSTNAME:
				"Please enter a first name of the secondary applicant",
			ADOPTION_SEC_CONTACT_SURNAME:
				"Please enter a surname of the secondary applicant",
			CONTACT_ADDRESSPOSTCODE: "Please enter your postcode",
			CONTACT_ADDRESSTOWN: "Please enter your town",
			CONTACT_ADDRESSCOUNTY: "Please enter your county",
			CONTACT_ADDRESSCOUNTRY: "Please enter your country",
			CONTACT_TELEPHONE: "Please enter a valid telephone number",
			CONTACT_EMAIL: "Please enter a valid email address",
			ADOPTION_PREFERRED_CENTRE: "Please choose your preferred rehoming centre",
		};

		if (propertyName.startsWith("ADOPTION_OTHER_BREED")) {
			return {
				name: "ADOPTION_OTHER_BREED",
				message: "Please enter the breeds you are interested in",
				property: "ADOPTION_OTHER_BREED",
			};
		}

		return { message: messages[propertyName] || "" };
	};

	return errors.map((error) => {
		if (error.name === "required") {
			error.message = "This field is required";
		} else if (error.params?.type === "integer") {
			error.message = "This field requires a number";
		}

		if (error.property) {
			const propertyName = error.property.split(":")[1];
			const propertyMessage = getMessageForProperty(propertyName);

			if (propertyMessage.message) {
				error.message = propertyMessage.message;
				if (propertyMessage.name) {
					error.name = propertyMessage.name;
					error.property = `${error.property.split(":")[0]}:${
						propertyMessage.property
					}`;
				}
			}
		}

		return error;
	});
}

const SubmitButton = () => {
	const { previousUrl, isLastStep } = useFormBuilderContext();

	const isClientSide = useClientSide();
	if (isClientSide) document.getElementById("error-summary")?.remove();
	return (
		<div className={styles.buttonwrapper}>
			{previousUrl && (
				<Button
					className={styles.backbutton}
					fullWidth
					id="back-button"
					variant="tertiary"
					url={previousUrl}
				>
					Back
				</Button>
			)}
			<Button
				className={styles.nextbutton}
				fullWidth
				id="submit-button"
				type="submit"
				variant="secondary"
			>
				{isLastStep ? "Submit" : "Next"}
			</Button>
		</div>
	);
};

const DisabledSubmitButton = () => {
	const { previousUrl } = useFormBuilderContext();

	const isClientSide = useClientSide();
	if (isClientSide) document.getElementById("error-summary")?.remove();
	return (
		<div className={styles.buttonwrapper}>
			{previousUrl && (
				<Button
					className={styles.backbutton}
					fullWidth
					id="back-button"
					variant="tertiary"
					url={previousUrl}
				>
					Back
				</Button>
			)}
			<Button
				className={styles.nextbutton}
				fullWidth
				id="submit-button"
				type="submit"
				variant="secondary"
				disabled={true}
			>
				Submit
			</Button>
		</div>
	);
};

export const FormBuilder: React.FC<Props> = ({
	schema,
	uiSchema,
	extraErrors,
	formData,
	widgets,
	isLastStep,
	previousStepUrl,
	// aerErrorTransformer,
	hasSubmitted,
	onChange,
	onSubmit,
}) => {
	const handleSubmit = (
		// biome-ignore lint/suspicious/noExplicitAny: Legit any
		data: IChangeEvent<unknown, RJSFSchema, any>,
		// biome-ignore lint/suspicious/noExplicitAny: Legit any
		event: React.FormEvent<any>,
	) => {
		onSubmit?.(data, event);
	};

	const chosenButton = hasSubmitted ? DisabledSubmitButton : SubmitButton;

	return (
		<FormBuilderContextProvider
			isLastStep={isLastStep}
			previousUrl={previousStepUrl}
		>
			<AddressManagerProvider>
				<JSONSchemaForm
					key={hasSubmitted ? "hs" : "hns"}
					validator={validator}
					schema={schema}
					uiSchema={uiSchema}
					extraErrors={extraErrors}
					formData={formData}
					onChange={onChange}
					widgets={widgets || defaultWidgets}
					noHtml5Validate={true}
					showErrorList={false}
					templates={{
						ErrorListTemplate,
						FieldTemplate: CustomFieldTemplate,
						ButtonTemplates: {
							SubmitButton: chosenButton,
						},
					}}
					onSubmit={handleSubmit}
					transformErrors={transformErrors}
					className={styles.form}
					focusOnFirstError
				/>
			</AddressManagerProvider>
		</FormBuilderContextProvider>
	);
};
export default FormBuilder;
